import * as React from 'react';
import S, { DataSignal } from 's-js';
import * as moment from 'moment';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { noDefault, noPropagation } from 'lib/eventWrappers';
import { tzmoment } from 'lib/tzmoment';

import { DispatchDashboardTab } from 'server/enums/DispatchDashboardTab';
import { PilotAssignment } from 'data/models/PilotAssignment';

import {
    DispatchDashboard,
    DashboardDayViewModel,
    DispatchEventViewModel,
    PilotJobOrderViewModel,
    ItineraryPointViewModel,
    MAX_SEARCH_RESULTS_SHOWN,
    ROLLING_EXPORT_HOURS
} from './model';

import { AddDispatchEventView } from './adddispatcheventview';
import { AddPilotJobOrderView } from './addpilotjoborderview';
import { DispatchEventDetailsView } from './dispatcheventdetailsview';

import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';
import { DateRangeSelectorView } from 'portcall/daterangeselector/view';
import { DownloadDataLink } from 'portcall/downloadlink/view';
import { PageLoadingIndicator } from 'core/actionloadingindicator/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';
import { PCTooltip } from 'portcall/pctooltip/view';
import { PilotsHeaderView, PilotsHeaderTab } from 'pilots/pilotsheader/view';
import { PilotJobOrderDetailsView, PilotJobOrderDetailsPopupViews } from 'pilots/pilotjoborderdetails/view';
import { SmallConflictCountBadgeWithTooltip } from 'portcall/issues/view';

// TH 01/23/2017 -- Original comment from pre-typescript conversion.
// TH: This represents the height of the navbar, Pilots header, and actions container in px.
// Need to update this factor if those heights change.

// Consolidating screenWidth & deviceWidth/availWidth into DeviceSize
interface DeviceSize {
    screenWidth : number;
    availWidth : number;
}

function getStickyHeaderFudgeFactor({ screenWidth, availWidth } : DeviceSize, tab : DispatchDashboardTab) {
    const STICKY_DAY_HEADER_FUDGE_FACTOR_MAIN_1 = 123,
          STICKY_DAY_HEADER_FUDGE_FACTOR_MAIN_2 = 158,
          STICKY_DAY_HEADER_FUDGE_FACTOR_MOBILE_1 = 90,
          STICKY_DAY_HEADER_FUDGE_FACTOR_MOBILE_2 = 158,
          MOBILE_THRESHOLD = 768;
        //MOBILE_THRESHOLD = @screen-size-tab-md;

    if (screenWidth <= MOBILE_THRESHOLD || availWidth <= MOBILE_THRESHOLD) {
        if (tab === DispatchDashboardTab.Search || tab === DispatchDashboardTab.BillingWorklist) return STICKY_DAY_HEADER_FUDGE_FACTOR_MOBILE_2;
        return STICKY_DAY_HEADER_FUDGE_FACTOR_MOBILE_1;
    }
    else if (tab === DispatchDashboardTab.Search || tab === DispatchDashboardTab.BillingWorklist) return STICKY_DAY_HEADER_FUDGE_FACTOR_MAIN_2;
    return STICKY_DAY_HEADER_FUDGE_FACTOR_MAIN_1;
};

export const DispatchDashboardView = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    <div
        className={cx('dispatchdashboard', {
            'allow-full-page-take-over': m.AllowFullPageTakeOver(),
            'showing-pjo-or-event': m.SelectedItemKey() != null,
        })}
    >
        {m.Model().Navbar() &&
            <PilotsHeaderView
                selectedTab={
                    m.Tab === DispatchDashboardTab.New ? PilotsHeaderTab.New :
                    (m.Tab === DispatchDashboardTab.Search || m.Tab === DispatchDashboardTab.BillingWorklist) ? PilotsHeaderTab.Search :
                    PilotsHeaderTab.Movements
                }
                title="Dispatch Dashboard"
                model={m.Model().Navbar()!}
            />
        }
        <SecondaryPageTitle model={m} />
        <PageControlBar model={m} />
        <InvalidSearchMsg model={m} />
        <PageLoadingIndicator loading={m.ModelLoading} />
        <PageBody model={m} />
        <ActionsMenu model={m} />
        {(m.SelectedItemKey() && m.SelectedItemKey()!.pjoId || m.SelectedDispatchEvent()) &&
            <div className="details-backdrop" onClick={() => m.SelectedItemKey(null)}></div>
        }
        <div className="pjo-container">
            <PilotJobOrderDetailsView modelContainer={m.PilotJobOrderDetails} />
        </div>
        <div className="dispatch-evt-container">
            <DispatchEventDetailsView modelContainer={m.SelectedDispatchEvent} />
        </div>
        <AddDispatchEventView modelContainer={m.AddDispatchEvent} />
        <AddPilotJobOrderView modelContainer={m.AddPilotJobOrder} />
        <PilotJobOrderDetailsPopupViews modelContainer={m.PilotJobOrderDetails} />
    </div>
);

let SecondaryPageTitle = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    <div className="secondary-page-title">
        <div className="title-main">{m.Tab === DispatchDashboardTab.BillingWorklist ? 'Billing Worklist' : ''}</div>
        <div className="title-sub">
            {m.Tab === DispatchDashboardTab.BillingWorklist &&
                <a href={m.BackToFullSearchLink!()}>View All</a>
            }
        </div>
    </div>
);

let PageControlBar = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    <div className={cx('pc-actions-links-header control-bar', {
            'secondary-page-title-positioning': m.Tab === DispatchDashboardTab.BillingWorklist,
            'pc-search-mobile' : m.Tab === DispatchDashboardTab.Search || m.Tab === DispatchDashboardTab.BillingWorklist
        })}>
        {m.Filter &&
            <input className="pc-reports-filter" placeholder="Filter By &mdash;"
                value={m.Filter()}
                onChange={e => m.Filter!(e.currentTarget.value)} 
                onKeyUp={e => e.key === 'Enter' ? m.UpdateModelKey() : () => {}} />
        }
        {m.DateRangeSelector && <DateRangeSelectorView model={m.DateRangeSelector} />}
        {(m.Filter || m.DateRangeSelector) && <SearchButton model={m} />}
        <ActionsMenuButton onClick={m.ToggleActionsMenu} />
    </div>
);

const SearchButton = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    <button
        className="pc-button search-button"
        disabled={m.ModelLoading()}
        onClick={m.UpdateModelKey}
    >
        {m.ModelLoading() ? 'Searching...' : m.ModelKeyDirty() ? 'Search' : 'Refresh'}
    </button>
);

const InvalidSearchMsg = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    <PCModal className="invalid-search-msg" shown={m.ShowInvalidSearchMsg} onBackDropClick={() => m?.ShowInvalidSearchMsg(false)}>
        <div className="modal-content pc-card">
            <div>
                The search parameters you've entered may overload your browser.
                Please enter a longer text filter or reduce the date range to 1 year or less.
            </div>
            <div className="controls">
                <button className="pc-button" onClick={() => m.ShowInvalidSearchMsg(false)}>
                    OK
                </button>
            </div>
        </div>
    </PCModal>
);

let ActionsMenu = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    m.ShowActionsMenu() ?
        <div className="pc-actions">
            <PCDismissible className="pc-actions-menu pc-card" onDismiss={() => m.ShowActionsMenu(false)}>
                {m.Tab === DispatchDashboardTab.Movements && m.Model().CanReserveBerths() &&
                    <div className="action-item">
                        <div>
                            <a href="#!addportcall">Add PortCall</a>
                        </div>
                        <div className="pc-action-item-descr">
                            Create a new PortCall.
                        </div>
                    </div>
                }
                {m.Tab === DispatchDashboardTab.Movements && m.Model().CanAddPilotJobOrders() &&
                    <div className="action-item">
                        <div>
                            <a href="" onClick={noDefault(m.MenuAction(m.ShowAddPilotJobOrderModal))}>Add Pilot Order</a>
                        </div>
                        <div className="pc-action-item-descr">
                            Add Pilot Orders to movements from existing PortCalls.
                            View movements without pilot orders.
                        </div>
                    </div>
                }
                {m.Tab === DispatchDashboardTab.Movements && m.Model().CanManageDispatchEvents() &&
                    <div className="action-item">
                        <div>
                            <a href="" onClick={noDefault(m.MenuAction(() => m.ShowAddDispatchEventModal(true)))}>Add Event</a>
                        </div>
                        <div className="pc-action-item-descr">
                            Add an Event to the Dispatch Dashboard.
                        </div>
                    </div>
                }
                <div className="action-item">
                    <div>
                        <a href="" onClick={noDefault(m.MenuAction(m.PrintPage))}>Print Schedule</a>
                    </div>
                    <div className="pc-action-item-descr">
                        Use the browser menu to print all movements in this view.
                    </div>
                </div>
                {(m.Tab === DispatchDashboardTab.Search || m.Tab === DispatchDashboardTab.BillingWorklist) &&
                    m.Model().CanExportSchedule() &&
                    <div className="action-item">
                        <div>
                            <CSVExportLink model={m} onClick={m.MenuAction(() => {})}>
                                Export to CSV
                            </CSVExportLink>
                        </div>
                        <div className="pc-action-item-descr">
                            Save to a CSV file for Excel.
                        </div>
                    </div>
                }
                {m.Tab === DispatchDashboardTab.Movements &&
                    <div className="action-item">
                        <div>
                            <a href="" onClick={noDefault(m.MenuAction(m.PrintRollingWindow))}>Print Next {ROLLING_EXPORT_HOURS} Hours</a>
                        </div>
                        <div className="pc-action-item-descr">
                            Use the browser menu to print the schedule for the next {ROLLING_EXPORT_HOURS} hours.
                        </div>
                    </div>
                }
                {m.Model().CanManageBills() &&
                    <div>
                        <div className="pc-admin-separator pc-card-admin-title">
                            Pilot Billing <div className="pc-admin-line"></div>
                        </div>
                        {m.Tab !== DispatchDashboardTab.BillingWorklist &&
                            <div className="action-item">
                                <div>
                                    <a href={m.ViewBillingWorklistLink()}>View Billing Worklist</a>
                                </div>
                                <div className="pc-action-item-descr">
                                    View and approve movements and events that are ready to export.
                                </div>
                            </div>
                        }
                        {m.BillingSystem() &&
                            <div className="action-item">
                                <div>
                                    <a href={'#!pendingexports?id=' + m.BillingSystem()!.Id()}>View Pending Exports</a>
                                </div>
                                <div className="pc-action-item-descr">
                                    Export approved bills to an external file.
                                </div>
                            </div>
                        }
                    </div>
                }
            </PCDismissible>
        </div> : null
);

const CSVExportLink = SuperCritical(({ model: m, onClick, children } : {
    model : DispatchDashboard,
    onClick? : React.MouseEventHandler<HTMLAnchorElement>,
    children? : React.ReactNode
}) =>
    <DownloadDataLink
        className="csv-export-link"
        data={m.CSVData}
        filename="movements.csv"
        onClick={onClick}
    >
        {children}
    </DownloadDataLink>
);

interface PageBodyProps {
    model : DispatchDashboard;
}

class PageBody extends SuperCriticalComponent<PageBodyProps> {
    ref : (el : HTMLDivElement | null) => void;
    recalcStickyHeaders = S.data(null);
    deviceSize : DataSignal<DeviceSize>;

    constructor(props : PageBodyProps) {
        super(props);

        let recalcStickyHeadersQueued = false;

        const recalcStickyHeadersAsync = () => {
            if (recalcStickyHeadersQueued) return;
            recalcStickyHeadersQueued = true;
            // TH 03/31/2017 -- setTimeout instead of requestAnimationFrame for smoother scroll performance
            setTimeout(() => {
                recalcStickyHeadersQueued = false;
                this.recalcStickyHeaders(null);
            }, 300);
        };
        
        this.deviceSize = S.data(this.getDeviceSize());

        this.ref = SuperCriticalReference(this, ({ model: m }, el : HTMLDivElement) => {
            // TH 01/24/2017 -- We need to recalc sticky headers whenever we do anything
            // that affects the overall page height or the height of any DashboardDayView elements.
            // We do this by subscribing to our viewmodel signals and triggering a recalc async.
            // That way the DOM gets a chance to update before we recalc.
            // Currently, each recalc involves visiting every DashboardDayView and checking its positioning.
            // In theory, we could optimize this to visit as few DashboardDayViews as possible.
            // But that'd involve more bookkeeping, and so far I haven't found much performance improvement.

            S(() => {
                // Recalc headers when the collection of days changes...
                m.Days();
                recalcStickyHeadersAsync();
            });

            m.Days.map(day => {
                S(() => {
                    // Recalc whenever the collection of dashboard items changes...
                    day.DashboardItems();
                    recalcStickyHeadersAsync();
                });

                day.DashboardItems.map(item => {
                    if (item.ItemType === 'PJO') {
                        // Recalc whenever we hide/show a PilotJobOrderRemarksRow element.
                        item.RemarksVisible();
                        recalcStickyHeadersAsync();
                    }
                });
            });

            const refreshDeviceSize = () => this.deviceSize(this.getDeviceSize());

            // TH 01/24/2017 -- Also recalc anytime window scrolls or resizes...
            window.addEventListener('scroll', recalcStickyHeadersAsync);
            window.addEventListener('resize', recalcStickyHeadersAsync);
            window.addEventListener('resize', refreshDeviceSize);

            S.cleanup(() => {
                window.removeEventListener('scroll', recalcStickyHeadersAsync);
                window.removeEventListener('resize', recalcStickyHeadersAsync);
                window.removeEventListener('resize', refreshDeviceSize);
            });
        });
    }

    srender({ model: m } : { model : DispatchDashboard }) {
        return (
            <div className={cx('page-body', {
                'pc-search-mobile'  : m.Tab === DispatchDashboardTab.Search || m.Tab === DispatchDashboardTab.BillingWorklist,
                'tab-new'           : m.Tab === DispatchDashboardTab.New,
                'tab-movements'     : m.Tab === DispatchDashboardTab.Movements,
                'tab-search'        : m.Tab === DispatchDashboardTab.Search,
                'tab-billing'       : m.Tab === DispatchDashboardTab.BillingWorklist
            })} ref={this.ref}>
                {m.Days().map(day =>
                    <DashboardDayView model={m} day={day} recalcStickyHeaders={this.recalcStickyHeaders} deviceSize={this.deviceSize} key={day.Date} />
                )}
                <LargeResultSetMessage model={m} />
            </div>
        );
    }

    private getDeviceSize() : DeviceSize {
        return {
            screenWidth: window.innerWidth,
            availWidth: window.screen.availWidth
        };
    }
}

interface DashboardDayProps {
    model : DispatchDashboard;
    day : DashboardDayViewModel;
    recalcStickyHeaders : DataSignal<null>;
    deviceSize : DataSignal<DeviceSize>;
}

class DashboardDayView extends SuperCriticalComponent<DashboardDayProps> {
    monitorRef : (el : HTMLDivElement | null) => void;
    stickyHeader = S.data(false);

    constructor(props : DashboardDayProps) {
        super(props);

        this.monitorRef = SuperCriticalReference(this, ({ model: m, recalcStickyHeaders, day, deviceSize }, el : HTMLDivElement) => {
            recalcStickyHeaders(); // subscribe to the recalc trigger
            if (el.offsetTop < window.scrollY + getStickyHeaderFudgeFactor(deviceSize(), m.Tab)) {
                this.stickyHeader(true);
            } else {
                this.stickyHeader(false);
            }
        });
    }

    srender({ model: m, day, deviceSize } : DashboardDayProps) {
        return (
            <div className={cx('dashboard-day', {
                    'sticky-day-header': this.stickyHeader()
                })}>
                <div className="header-scroll-monitor" ref={this.monitorRef}></div>
                <DashboardDayHeader day={day} stickyHeader={this.stickyHeader()} deviceSize={deviceSize} tab={m.Tab} />
                <div className="day-spacing"></div>
                <DashboardDayTable model={m} day={day} />
            </div>
        );
    }
}

export const DateHeaderView = SuperCritical(({ moment: m, style } : { moment : moment.Moment, style? : React.CSSProperties }) =>
    <div className="day-header" style={style}>
        <span className="month">{m.format('MMMM')}</span>
        {' '}
        <span className="date">{m.format('DD')}</span>
        {' '}
        <span className="year">{m.format('YYYY')}</span>
        {' '}
        <span className="pipe">&nbsp;|&nbsp;</span>
        {' '}
        <span className="day-of-week">{m.format('dddd')}</span>
    </div>
);

let DashboardDayHeader = SuperCritical(({ day, stickyHeader, deviceSize, tab } : { 
    day : DashboardDayViewModel, 
    stickyHeader : boolean, 
    deviceSize : DataSignal<DeviceSize>,
    tab : DispatchDashboardTab
}) =>
    <DateHeaderView moment={day.DateMoment} style={{ top: stickyHeader ? getStickyHeaderFudgeFactor(deviceSize(), tab) + 'px' : '' }} />
);

let DashboardDayTable = SuperCritical(({ day, model: m } : { day : DashboardDayViewModel, model : DispatchDashboard }) =>
    <table className="pilot-job-orders">
        <DashboardDayTableHeader model={m} />
        <tbody className="pc-card">
            {day.DashboardItems().map(item =>
                item.ItemType === 'PJO' ? [
                    <PilotJobOrderMainRow pjo={item} model={m} key={'pjo-main-' + item.Model.Id()} />,
                    <PilotJobOrderRemarksRow pjo={item} model={m} key={'pjo-remarks-' + item.Model.Id()} />
                ] : [
                    <DispatchEventRow event={item} model={m} key={'dispatch-evt-' + item.Model.Id()} />
                ]
            )}
        </tbody>
    </table>
);

let DashboardDayTableHeader = SuperCritical(({ model: m } : { model : DispatchDashboard }) => {
    const colNames = m.ColumnNames();
    return (
        <thead>
            <tr>
                <th className="pilot-flag">{/*Empty column header for pilot flag*/}</th>
                <th className="pc-text-smalllabel movement-type">{colNames.MovementType}</th>
                <th className="pc-text-smalllabel est-time">{colNames.EstimatedTime}</th>
                <th className="pc-text-smalllabel actual-time">{colNames.ActualTime}</th>
                <th className="pc-text-smalllabel vessel">{colNames.VesselName}</th>
                <th className="pc-text-smalllabel location">{colNames.Location}</th>
                <th className="mvmt-remarks-badge-container"></th>
                <th className="pc-text-smalllabel agent">{colNames.AgencyCode}</th>
                <th className="pc-text-smalllabel right-aligned-column tonnage">{colNames.GrossTonnage}</th>
                <th className="pc-text-smalllabel right-aligned-column length">{colNames.Length}</th>
                <th className="pc-text-smalllabel right-aligned-column beam">{colNames.Beam}</th>
                <th className="pc-text-smalllabel right-aligned-column draft">{colNames.DeepDraft}</th>
                <th className="pc-text-smalllabel right-aligned-column air-draft">{colNames.AirDraft}</th>
                <th className="pc-text-smalllabel tug-co">{colNames.TugCompany}</th>
                <th className="pc-text-smalllabel side-to">{colNames.SideTo}</th>
                <th className="pc-text-smalllabel last-column pilots">{m.Model().Navbar()?.CanViewRotation() ? colNames.Pilot : ''}</th>
            </tr>
        </thead>
    );
});

let PilotJobOrderMainRow = SuperCritical(({ pjo, model: m } : { pjo : PilotJobOrderViewModel, model : DispatchDashboard }) =>
    <tr className={cx('dashboard-item-row pilot-job-order-row', {
            underway: !!pjo.Model.Start.Actual(),
            complete: !!pjo.Model.End.Actual(),
            cancelled: !!pjo.Model.Cancellation(),
            selected: pjo.Selected(),
            showRemarks: pjo.RemarksVisible()
        })}
        onClick={() => m.SelectedItemKey({ pjoId: pjo.Model.Id() })}>
        <PilotFlagCell pjo={pjo} />
        <MovementTypeCell pjo={pjo} />
        <EstimatedTimeCell pjo={pjo} />
        <ActualTimeCell pjo={pjo} />
        <VesselNameCell pjo={pjo} />
        <MovementLocationCell pjo={pjo} />
        <RemarksBadgeCell pjo={pjo} />
        <AgencyCodeCell pjo={pjo} />
        <GrossTonnageCell pjo={pjo} />
        <LengthCell pjo={pjo} />
        <BeamCell pjo={pjo} />
        <DeepDraftCell pjo={pjo} />
        <AirDraftCell pjo={pjo} />
        <TugCoCell pjo={pjo} />
        <SideToCell pjo={pjo} />
        <PilotsCell pjo={pjo} model={m} />
    </tr>
);

let PilotFlagCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className={cx('pilot-flag', {
            'show-flag': !!pjo.Model.Start.Actual() && !pjo.Model.End.Actual()
        })}>
        <PCTooltip title={() => 'Pilot Aboard'} className="pilot-tooltip" />
        {!pjo.Model.Start.Actual() && !pjo.Model.End.Actual() &&
            <SmallConflictCountBadgeWithTooltip conflicts={() => pjo.AllConflicts()} />
        }
    </td>
);

let MovementTypeCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="movement-type">{pjo.MovementTypeText()}</td>
);

let EstimatedTimeCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className={cx('est-time confirmation-container', {
            confirmed: pjo.Model.StartTimeConfirmed(),
            textForActual: pjo.Model.Start.Actual() !== null
        })}>
        <span className="confirmation-text">
            {tzmoment(pjo.Model.Start.Estimated()).format('HH:mm')}
        </span>
        <span className="fa fa-question unfirmed-icon"></span>
        <span className={cx("fa fa-check confirmed-icon", {
            iconForActual: pjo.Model.Start.Actual() !== null
        })}></span>
    </td>
);

let ActualTimeCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="actual-time">
        {pjo.Model.Start.Actual() ? tzmoment(pjo.Model.Start.Actual()!).format('HH:mm') : ''}
        {/* DC 4/15/2017: hide --:-- to reduce noise */}
    </td>
);

let VesselNameCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="vessel">{pjo.VesselNameText()}</td>
);

let MovementLocationCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="location">
        <LocationNameView point={pjo.FromPoint} />
        {' '}
        <span className="is-not-berth">to</span>
        {' '}
        <LocationNameView point={pjo.ToPoint} />
    </td>
);

let LocationNameView = SuperCritical(({ point: pt } : { point : () => ItineraryPointViewModel | null }) =>
    <span className={cx({
            'is-not-berth': !pt() && !pt()!.Model.Location() && !!pt()!.Model.Location()!.Berth()
        })}>
        {pt() && pt()!.Model.Location() && pt()!.Model.Location()!.Name()}
    </span>
);

let RemarksBadgeCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="mvmt-remarks-badge-container">
        {!!pjo.Model.Remarks() &&
            <span className="mvmt-remarks-badge fa fa-file-text" onClick={noPropagation(pjo.ToggleRemarksVisible)}></span>
        }
    </td>
);

let AgencyCodeCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="agent">{pjo.AgencyCodeText()}</td>
);

let GrossTonnageCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="tonnage right-aligned-column">{pjo.GrossTonnageText()}</td>
);

let LengthCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="length right-aligned-column">{pjo.LengthText()}</td>
);

let BeamCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="beam right-aligned-column">{pjo.BeamText()}</td>
);

let DeepDraftCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className={cx('draft right-aligned-column confirmation-container', {
            confirmed: pjo.Model.DeepDraftConfirmed(),
            blank: pjo.Model.EffectiveDeepDraft.Num() === null || pjo.Model.EffectiveDeepDraft.UOM() === null,
            // TH 04/11/2017 -- confirmed styling trumps conflict styling
            conflicted: !pjo.Model.DeepDraftConfirmed() && pjo.HasDraftConflict()
        })}>
        <span className="confirmation-text">{pjo.DeepDraftText()}</span>
        <span className="fa fa-question unfirmed-icon"></span>
        <span className="fa fa-check confirmed-icon"></span>
        {/*<span className="fa fa-times conflict-icon"></span>*/}
    </td>
);

let AirDraftCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className={cx('air-draft right-aligned-column confirmation-container', {
            confirmed: pjo.Model.DeepDraftConfirmed(),
            blank: pjo.Model.AirDraft.Num() === null || pjo.Model.AirDraft.UOM() === null,
        })}>
        <span className="confirmation-text">{pjo.AirDraftText()}</span>
    </td>
);

let TugCoCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="tug-co">{pjo.TugCompanyText()}</td>
);

let SideToCell = SuperCritical(({ pjo } : { pjo : PilotJobOrderViewModel }) =>
    <td className="side-to">{pjo.SideToText()}</td>
);

let PilotsCell = SuperCritical(({ pjo, model: m } : { pjo : PilotJobOrderViewModel, model : DispatchDashboard }) =>
    <td className="pilots">
        {!m.Model().Navbar()?.CanViewRotation() ? <div></div> : !pjo.Pilots().length ?
            <span>--</span> :
            pjo.Pilots().map((p, i) => [
                <PilotCodeView pilotAssignment={p} key={'pilot-' + p.PilotId()} />,
                i < pjo.Pilots().length - 1 ?
                    <span className="pilot-separator" key={'sep-' + i}>,</span> : null
            ])
        }
    </td>
);

let PilotCodeView = SuperCritical(({ pilotAssignment : p } : { pilotAssignment : PilotAssignment }) =>
    <span className={cx('confirmation-container', {
            confirmed: p.PilotConfirmed()
        })}>
        <span className="confirmation-text">
            {p.Pilot() && p.Pilot()!.EffectiveCode()}
        </span>
        <span className="fa fa-question unfirmed-icon"></span>
        <span className="fa fa-check confirmed-icon"></span>
    </span>
);

let PilotJobOrderRemarksRow = SuperCritical(({ pjo, model: m } : { pjo : PilotJobOrderViewModel, model : DispatchDashboard }) =>
    <tr className={cx('dashboard-item-row mvmt-remarks-row', {
                'remarks-visible': pjo.RemarksVisible(),
                complete: !!pjo.Model.End.Actual(),
                selected: pjo.Selected()
        })}
        onClick={() => m.SelectedItemKey({ pjoId: pjo.Model.Id() })}>
        <td className="pilot-flag"></td>
        <td className="movement-type"></td>
        <td className="est-time"></td>
        <td className="actual-time"></td>
        <td className="remarks-text" colSpan={11}>
            {pjo.RemarksVisible() && pjo.Model.Remarks()}
        </td>
    </tr>
);

let DispatchEventRow = SuperCritical(({ event, model: m } : { event : DispatchEventViewModel, model : DispatchDashboard }) => 
    // DC: Billable events are now Private events; so if it's set to Private and the user has permissions
    // to see the Rotation, they can see the event. Public events are viewable by all.
    // Note: this may cause some wonkiness with empty Date headers when there's only 1 private event.
    event.Model.Billable() && m.Model().Navbar()?.CanViewRotation() || !event.Model.Billable() ? 
        <tr className={cx('dashboard-item-row dispatch-event-row', {
            selected: event.Selected()
        })}
        onClick={() => m.SelectedItemKey({ dispatchEventId: event.Model.Id() })}>
            <td className="pilot-flag">
            </td>
            <td className="movement-type"></td>
            <td className="est-time">
                {tzmoment(event.Model.Start()).format('HH:mm')}
            </td>
            <td className="actual-time"></td>
            <td className="dispatch-descr" colSpan={11}>{event.Model.Description()}</td>
        </tr> 
    : null
);

const LargeResultSetMessage = SuperCritical(({ model: m } : { model : DispatchDashboard }) =>
    m.ResultSetIsTruncated() ?
        <div className="large-result-set-msg pc-card">
            Your search returned a large number of results. The first
            {' '}
            {MAX_SEARCH_RESULTS_SHOWN}
            {' '}
            are shown.
            View entire results as a <CSVExportLink model={m}>CSV file</CSVExportLink>?
        </div> :
        null
);



// WEBPACK FOOTER //
// ./apps/pilots/dispatchdashboard/view.tsx